import Cache from '~/utils/Cache';
import log from '~/utils/log';

export type LinkMap = {
    id: string;
    link: string;
};

const CACHE_DURATION = 3 * 60 * 60 * 1000; // 3 hours
const cache = new Cache<LinkMap[]>(CACHE_DURATION);
type StoryWithProductLink = { content?: { productId?: string }; full_slug?: string };

// Exclude fields that are not needed for the product links to reduce the payload size.
// We only need `content.productId` + top-level `full_slug` for checkout/cart flows.
const EXCLUDING_FIELDS = [
    'storyWrapper',
    'seo',
    'body',
    'customTabs',
    'AI_SEO',
    'settings',
    'canonicalLink',
    'recommendation',
    'component',
    '_uid',
    '_editable'
].join(',');

export function useStoryblokProductsCache() {
    const { country, lang } = useLanguage();
    const runtimeConfig = useRuntimeConfig();
    const headers = useRequestHeaders(['cookie']);
    const cacheKey = computed(() => `productLinks-${country.value}-${lang.value}`);

    async function getStoryblokProductLinks() {
        const data = cache.get(cacheKey.value);
        if (data) {
            log.debug('[storyblok] using cached data');
            return data;
        }

        try {
            log.debug('[storyblok] fetching from storyblok...');

            const startsWith = [runtimeConfig.public.storyblokAppFolder, country.value, 'shop/detail']
                .filter(Boolean)
                .join('/');

            const storyData = await $fetch<unknown[]>(StoryblokProxyEndpoint, {
                query: {
                    type: 'stories',
                    language: lang.value,
                    params: JSON.stringify({
                        starts_with: startsWith,
                        excluding_fields: EXCLUDING_FIELDS
                    })
                },
                headers
            });

            const productLinks = getProductLinks(storyData);

            cache.set(cacheKey.value, productLinks);
            return productLinks;
        } catch (err) {
            const errObj = err && typeof err === 'object' ? (err as Record<string, unknown>) : {};
            throw createError({ ...errObj, fatal: true });
        }
    }

    return { getStoryblokProductLinks };
}

const getProductLinks = (productStories: unknown[]) => {
    if (!productStories) return [];

    return (productStories as StoryWithProductLink[])
        .filter((item): item is StoryWithProductLink & { content: { productId: string }; full_slug: string } =>
            Boolean(item.content?.productId && item.full_slug)
        )
        .map((item) => ({ id: item.content.productId, link: item.full_slug }));
};
